import { SpinResponse, Line, ReelWinPosition } from '@cgs/data-access';
import {
  ExpandedWinLineActionComponent,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GameStateMachine,
  ExpandedShortWinlinesActionParams,
  IGameStateMachineProvider,
  ReelsSoundModel,
  ResourcesComponent,
  SpecialNWaysAction,
  SpinConfig,
  RESOURCES_COMPONENT_TOKEN,
  ExpandedShortWinlinesAction,
  ExpandedWinlinesActions,
  GAME_WIN_COMPONENT_TOKEN,
  GameWinComponent,
} from '@cgs/machines';
import { Container, FunctionAction, ParallelAction, Vector2 } from '@cgs/wge';

import { CORSICA_WIN_LINE_POPUP_TOKEN, CorsicaWinLinePopup } from './corsica-winline-popup';

export class CorsicaWinLineActionComponent extends ExpandedWinLineActionComponent {
  private readonly gameWinComponent: GameWinComponent;
  private readonly gameStateMachine: GameStateMachine<SpinResponse>;
  private readonly winLinePopup: CorsicaWinLinePopup;

  constructor(container: Container) {
    const offset =
      container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN).slot.findById('slot_start_0')
        ?.size || Vector2.Zero;
    const slotFrameOffset = offset.add(new Vector2(7, 0)); //7px it's the size of frame between reels

    const lineOptions = {
      lineColor: '#ffffff',
      lineWidth: 6,
      glowIntensity: 2,
      glowSize: 6,
      offset: slotFrameOffset,
    };

    super(container, {
      lineOptions,
      winlineHolderId: 'winline_holder',
    });

    this.gameWinComponent = container.resolve<GameWinComponent>(GAME_WIN_COMPONENT_TOKEN);

    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;

    this.winLinePopup = container.resolve<CorsicaWinLinePopup>(CORSICA_WIN_LINE_POPUP_TOKEN);

    // The SDK requires addWin before the spin flow completes: TrifectaMainGameHook
    // calls onSuccess() on regularSpins.entered, while the win line animation (and
    // an addWin embedded in it) would only start after that. The animation state
    // always precedes regularSpins/shortWinLines, so report the win here.
    this.gameStateMachine.animation.entered.listen(() => {
      const response = this.gameStateMachine.curResponse;

      if (!response.isFreeSpins && response.totalWin) {
        this.gameWinComponent.addWin(response.totalWin);
      }
    });
  }

  public update(
    reelsSoundModel: ReelsSoundModel,
    winLines: Line[],
    viewReels: number[][],
    spinConfig: SpinConfig,
    winPositions: ReelWinPosition[]
  ): void {
    const lineOption: ExpandedShortWinlinesActionParams = {
      container: this.container,
      lineDrawer: this._lineDrawer,
      spinConfig: spinConfig,
      winLines,
      winPositions: winPositions,
    };

    const shortWinLineAction = new ParallelAction([
      new ExpandedShortWinlinesAction(lineOption),
      new FunctionAction(() => {
        if (!this.gameStateMachine.curResponse.isFreeSpins) {
          if (this.gameStateMachine.curResponse.totalWin) {
            this.winLinePopup.show(this.gameStateMachine.curResponse.totalWin);
          }
        }
      }),
    ]);

    const specialLineAction = new SpecialNWaysAction(
      this.container,
      winLines,
      spinConfig,
      winPositions
    );

    // Unlike the base component, the short pass is not prepended here:
    // CorsicaGameStateMachine routes base-game spins through shortWinLines, so
    // it has already played (with the win popup) by the time regularSpins
    // starts this action — prepending it would flash and pop the win twice.
    this.winLineAction = new ExpandedWinlinesActions(lineOption, this.repeatWinLineSound);
    this.shortWinLineAction = shortWinLineAction;
    this.specialLineAction = specialLineAction;

    this.enableFade(winLines, winPositions);
  }
}
